import { useState, useRef, useEffect, useCallback } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  UploadCloud, Camera, X, Sparkles, Loader2, ImagePlus, CameraOff, Zap, Wand2, ScanBarcode, Layers,
} from "lucide-react";
import { toast } from "sonner";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { api } from "@/lib/apiClient";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { useFields } from "@/context/FieldsContext";
import { resolvedEnabledFields } from "@/lib/fields";

const MAX = 10;

export const SmartCaptureModal = ({ open, onOpenChange, onProcessed, onBarcodeDetected }) => {
  const { config } = useFields();
  const [autoFields, setAutoFields] = useState(true);
  const [multiItem, setMultiItem] = useState(false);
  const [barcodeMode, setBarcodeMode] = useState(false);
  const [lastCode, setLastCode] = useState(null);
  const barcodeReaderRef = useRef(null);
  const barcodeControlsRef = useRef(null);
  const lastScanRef = useRef({ code: null, at: 0 });
  const [tab, setTab] = useState("upload");
  const [queue, setQueue] = useState([]); // {id, file, preview}
  const [dragging, setDragging] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, done: 0, failed: 0 });
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [camError, setCamError] = useState(null);
  const [camReady, setCamReady] = useState(false);
  const criteria = resolvedEnabledFields(config);

  const addFiles = useCallback((files) => {
    const imgs = Array.from(files).filter((f) => f.type.startsWith("image/"));
    setQueue((prev) => {
      const room = MAX - prev.length;
      if (room <= 0) {
        toast.error(`Max ${MAX} images per batch`);
        return prev;
      }
      const next = imgs.slice(0, room).map((file) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        preview: URL.createObjectURL(file),
      }));
      if (imgs.length > room) toast.warning(`Only added ${room}; batch limit is ${MAX}`);
      return [...prev, ...next];
    });
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCamReady(false);
  }, []);

  const startCamera = useCallback(async () => {
    setCamError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCamReady(true);
    } catch (e) {
      setCamError("Camera unavailable or permission denied. Use Batch Upload instead.");
      setCamReady(false);
    }
  }, []);

  useEffect(() => {
    if (open && tab === "camera") startCamera();
    else stopCamera();
    return stopCamera;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab]);

  useEffect(() => {
    if (!open) {
      stopCamera();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open) {
      setAutoFields(config.auto_fields);
      setMultiItem(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const stopBarcode = useCallback(() => {
    try {
      barcodeControlsRef.current?.stop();
    } catch {
      /* ignore */
    }
    barcodeControlsRef.current = null;
  }, []);

  const handleDetected = useCallback(
    (code) => {
      if (!code) return;
      const now = Date.now();
      // de-dupe rapid repeats of the same code
      if (lastScanRef.current.code === code && now - lastScanRef.current.at < 3000) return;
      lastScanRef.current = { code, at: now };
      setLastCode(code);
      onBarcodeDetected?.(code);
    },
    [onBarcodeDetected]
  );

  const startBarcode = useCallback(async () => {
    if (!videoRef.current) return;
    if (!barcodeReaderRef.current) barcodeReaderRef.current = new BrowserMultiFormatReader();
    try {
      barcodeControlsRef.current = await barcodeReaderRef.current.decodeFromVideoElement(
        videoRef.current,
        (result) => {
          if (result) handleDetected(result.getText());
        }
      );
    } catch (e) {
      /* NotFound errors are continuous; ignore */
    }
  }, [handleDetected]);

  // Barcode scanner lifecycle
  useEffect(() => {
    if (open && tab === "camera" && camReady && barcodeMode) {
      startBarcode();
    } else {
      stopBarcode();
    }
    return stopBarcode;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab, camReady, barcodeMode]);

  const snap = () => {
    const video = videoRef.current;
    if (!video || !camReady) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `capture-${Date.now()}.jpg`, { type: "image/jpeg" });
      addFiles([file]);
      toast.success("Photo captured");
    }, "image/jpeg", 0.92);
  };

  const removeFromQueue = (id) =>
    setQueue((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) URL.revokeObjectURL(target.preview);
      return prev.filter((p) => p.id !== id);
    });

  const reset = () => {
    queue.forEach((q) => URL.revokeObjectURL(q.preview));
    setQueue([]);
  };

  const process = async () => {
    if (queue.length === 0) return;
    const batch = [...queue];
    setProcessing(true);
    setProgress({ current: 0, total: batch.length, done: 0, failed: 0 });
    const staged = [];
    let failed = 0;

    for (let i = 0; i < batch.length; i++) {
      // show which image is currently being analyzed
      setProgress({ current: i + 1, total: batch.length, done: i, failed });
      // yield a frame so the bar/label paints before the request blocks on await
      await new Promise((r) => requestAnimationFrame(() => r()));
      try {
        const res = await api.analyzeOne(batch[i].file, { auto_fields: autoFields, fields: config.fields }, multiItem);
        const got = Array.isArray(res.items) ? res.items : [];
        if (got.length) {
          got.forEach((it) => staged.push({ ...it, preview: batch[i].preview }));
        } else {
          throw new Error("empty result");
        }
      } catch (e) {
        failed += 1;
        staged.push({
          staging_id: `fail-${i}-${Date.now()}`,
          item_name: "Unidentified Item",
          description: "",
          category: "Other",
          quantity: 1,
          extracted_text: "",
          source_image_url: null,
          preview: batch[i].preview,
        });
      }
      // mark this image complete -> bar advances incrementally
      setProgress({ current: i + 1, total: batch.length, done: i + 1, failed });
    }

    setProcessing(false);
    setProgress({ current: 0, total: 0, done: 0, failed: 0 });

    const ok = staged.length - failed;
    if (ok > 0) toast.success(`Scanned ${ok} item${ok === 1 ? "" : "s"} successfully!`);
    if (failed > 0) toast.warning(`${failed} image${failed === 1 ? "" : "s"} couldn't be analyzed — review them`);
    onProcessed(staged);
    setQueue([]);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!processing) { onOpenChange(o); if (!o) reset(); } }}>
      <DialogContent
        data-testid="camera-modal"
        className="bg-card border-border max-w-2xl p-0 overflow-hidden gap-0"
      >
        <DialogHeader className="px-6 py-4 border-b border-border">
          <DialogTitle className="font-heading text-slate-50 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" /> Smart Capture
          </DialogTitle>
          <DialogDescription className="sr-only">
            Upload or capture product photos for AI analysis
          </DialogDescription>
        </DialogHeader>

        {/* Analysis criteria bar */}
        <div data-testid="scan-criteria-bar" className="px-6 pt-4">
          <div className="rounded-xl border border-border bg-secondary/20 p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs font-semibold text-slate-100 flex items-center gap-1.5">
                  <Wand2 className="h-3.5 w-3.5 text-accent" /> Automatic fields
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {autoFields
                    ? "AI auto-detects name, description & any visible text"
                    : "AI captures only your defined fields below"}
                </p>
              </div>
              <Switch
                data-testid="scan-auto-fields-switch"
                checked={autoFields}
                onCheckedChange={setAutoFields}
              />
            </div>

            <div className="mt-3 pt-3 border-t border-border/60 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${multiItem ? "bg-primary/15 text-primary" : "bg-secondary text-muted-foreground"}`}>
                  <Layers className="h-3.5 w-3.5" />
                </span>
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-slate-100">Multi-item scan</div>
                  <p className="text-[11px] text-muted-foreground">
                    Detect several items in one photo (e.g. a shelf of books)
                  </p>
                </div>
              </div>
              <Switch
                data-testid="multi-item-switch"
                checked={multiItem}
                onCheckedChange={setMultiItem}
              />
            </div>
            {criteria.length > 0 && (
              <div className="mt-2.5 flex flex-wrap gap-1.5" data-testid="scan-criteria-chips">
                {criteria.map((f) => (
                  <Badge
                    key={f.id}
                    variant="outline"
                    className="text-[10px] bg-primary/10 text-primary border-primary/25"
                  >
                    {f.resolvedLabel}
                  </Badge>
                ))}
              </div>
            )}
            {criteria.length === 0 && !autoFields && (
              <p className="mt-2 text-[11px] text-amber-400">
                No active fields — add some in Fields & AI Criteria, or turn Automatic fields on.
              </p>
            )}
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <div className="px-6 pt-4">
            <TabsList className="grid grid-cols-2 w-full bg-secondary/40">
              <TabsTrigger data-testid="tab-upload" value="upload" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                <UploadCloud className="h-4 w-4" /> Batch Upload
              </TabsTrigger>
              <TabsTrigger data-testid="tab-camera" value="camera" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                <Camera className="h-4 w-4" /> Live Camera
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="px-6 py-4">
            <TabsContent value="upload" className="mt-0">
              <div
                data-testid="file-upload-dropzone"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
                className={`cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
                  dragging ? "border-primary bg-primary/10" : "border-border bg-secondary/20 hover:bg-secondary/40"
                }`}
              >
                <ImagePlus className="mx-auto h-10 w-10 text-primary mb-3" />
                <p className="text-sm font-medium text-slate-100">
                  Drop up to {MAX} images, or click to browse
                </p>
                <p className="text-xs text-muted-foreground mt-1">PNG, JPG, WEBP · batch scanning supported</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  data-testid="file-input"
                  onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                />
              </div>
            </TabsContent>

            <TabsContent value="camera" className="mt-0">
              <div className="mb-3 rounded-xl border border-border bg-secondary/20 p-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${barcodeMode ? "bg-rose-500/15 text-rose-400" : "bg-secondary text-muted-foreground"}`}>
                    <ScanBarcode className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-slate-100">Barcode mode</div>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {barcodeMode ? "Align a barcode with the line to log it instantly" : "Auto-detect & instantly log barcodes / QR codes"}
                    </p>
                  </div>
                </div>
                <Switch
                  data-testid="barcode-mode-switch"
                  checked={barcodeMode}
                  onCheckedChange={setBarcodeMode}
                />
              </div>

              <div className="relative overflow-hidden rounded-xl border border-border bg-black aspect-video">
                <video ref={videoRef} playsInline muted className="h-full w-full object-cover" data-testid="webcam-viewfinder" />
                {camReady && !barcodeMode && (
                  <>
                    <div className="scanline-bar" />
                    <div className="pointer-events-none absolute inset-4 rounded-lg border-2 border-accent/50" />
                    <div className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-black/60 px-2.5 py-1 text-[10px] font-medium text-accent">
                      <span className="h-1.5 w-1.5 rounded-full bg-accent pulse-dot" /> LIVE
                    </div>
                  </>
                )}
                {camReady && barcodeMode && (
                  <div data-testid="barcode-overlay">
                    <div className="barcode-frame pointer-events-none" />
                    <div className="barcode-line" />
                    <div className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-rose-500/20 px-2.5 py-1 text-[10px] font-semibold text-rose-300 ring-1 ring-rose-500/40">
                      <ScanBarcode className="h-3 w-3" /> BARCODE MODE
                    </div>
                  </div>
                )}
                {!camReady && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground gap-2 p-4 text-center">
                    {camError ? <CameraOff className="h-8 w-8" /> : <Loader2 className="h-8 w-8 animate-spin" />}
                    <span className="text-xs max-w-xs">{camError || "Starting camera…"}</span>
                  </div>
                )}
              </div>

              {barcodeMode ? (
                <div data-testid="barcode-status" className="mt-3 rounded-lg border border-border bg-secondary/20 px-3 py-2.5 text-center">
                  {lastCode ? (
                    <span className="text-xs text-slate-200">
                      Last scanned: <span className="font-mono text-rose-300" data-testid="last-barcode-value">{lastCode}</span> — added to inventory
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-rose-400 pulse-dot" /> Scanning for barcodes…
                    </span>
                  )}
                </div>
              ) : (
                <Button
                  data-testid="snap-photo-button"
                  onClick={snap}
                  disabled={!camReady}
                  className="w-full mt-3 gap-2 bg-accent text-accent-foreground hover:bg-accent/90 font-semibold disabled:opacity-40"
                >
                  <Camera className="h-4 w-4" /> Snap Photo
                </Button>
              )}
            </TabsContent>
          </div>
        </Tabs>

        {/* Staging queue */}
        {queue.length > 0 && (
          <div className="px-6 pb-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Staging Queue ({queue.length}/{MAX})
              </span>
              <button onClick={reset} className="text-xs text-muted-foreground hover:text-destructive">Clear all</button>
            </div>
            <div data-testid="staging-queue-list" className="grid grid-cols-5 gap-2 max-h-40 overflow-y-auto">
              {queue.map((q) => (
                <div key={q.id} className="relative group aspect-square rounded-lg overflow-hidden border border-border">
                  <img src={q.preview} alt="staged" className="h-full w-full object-cover" />
                  <button
                    onClick={() => removeFromQueue(q.id)}
                    className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border">
          {processing && progress.total > 0 && (
            <div data-testid="analysis-progress" className="mb-4 fade-up">
              <div className="flex items-center justify-between mb-2">
                <span data-testid="analysis-progress-label" className="text-xs font-medium text-slate-200 flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                  Analyzing image {progress.current} of {progress.total}… Please wait
                </span>
                <span className="text-xs font-mono tabular-nums text-primary">
                  {Math.round((progress.done / progress.total) * 100)}%
                </span>
              </div>
              <Progress
                data-testid="analysis-progress-bar"
                value={(progress.done / progress.total) * 100}
                className="h-2 bg-secondary"
              />
              <div className="mt-1.5 text-[11px] text-muted-foreground">
                {progress.done} completed{progress.failed > 0 ? ` · ${progress.failed} failed` : ""}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground">
              {queue.length === 0 ? "Add images to begin" : `${queue.length} image${queue.length === 1 ? "" : "s"} ready`}
            </span>
            <Button
              data-testid="process-ai-button"
              onClick={process}
              disabled={queue.length === 0 || processing}
              className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold disabled:opacity-40 min-w-[160px]"
            >
              {processing ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Analyzing…</>
              ) : (
                <><Zap className="h-4 w-4" /> Process with AI</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
